/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { BasicCard } from 'bundles/cds-labs/components';
import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { MODULE_LAYOUTS } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import PreservedUtmButton from 'bundles/cds-labs/components/Modules/components/PreservedUtmButton';
import PreservedUtmLink from 'bundles/cds-labs/components/Modules/components/PreservedUtmLink';

export type PartnerListModulePartner = {
  id: string;
  description?: string | null;
  logo?: ImageType | null;
  name?: string | null;
  target?: string | null;
};

type CallToAction = {
  label: string;
  target: string;
};

type Props = {
  borders?: boolean | null;
  cta?: CallToAction | null;
  description?: string | null;
  partnersCollection?: PartnerListModulePartner[] | null;
  tagline?: string | null;
  title?: string | null;
} & ModuleLayoutConfig;

const styles = {
  tagline: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  description: css`
    margin-bottom: var(--cds-spacing-200);
    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  ctaSection: css`
    margin-top: var(--cds-spacing-100);
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,

  logoContainer: css`
    list-style-type: none;
    padding-inline-start: 0;
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: start;
    gap: var(--cds-spacing-300);

    ${breakpoints.down('md')} {
      flex-wrap: wrap;
      gap: var(--cds-spacing-200);
      justify-content: flex-start;
    }

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-100);
    }
  `,

  logoItem: css`
    display: flex;
    align-items: start;
    justify-content: center;
    flex-direction: column;
    flex: 1;

    ${breakpoints.down('md')} {
      min-width: calc((100% - var(--cds-spacing-200)) / 4 - 8px);
      max-width: calc((100% - var(--cds-spacing-200)) / 4 - 8px);
      margin-bottom: var(--cds-spacing-100);
    }
    ${breakpoints.down('sm')} {
      min-width: calc((100% - var(--cds-spacing-200)) / 3 - 6px);
      max-width: calc((100% - var(--cds-spacing-200)) / 3 - 6px);
    }
    ${breakpoints.down('xs')} {
      min-width: calc((100% - var(--cds-spacing-100)) / 2 - 4px);
      max-width: calc((100% - var(--cds-spacing-100)) / 2 - 4px);
    }
  `,

  logoWrapper: css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: var(--cds-spacing-100);
    width: 100%;
    height: 100px;
    background: var(--cds-color-white-0);
  `,

  logoWrapperWithBorder: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 8px;
  `,
};

export function PartnerListModule(props: Props) {
  const { partnersCollection, tagline, description, cta, borders } = props;
  const track = useTracker();

  const imageStyle = {
    objectFit: 'scale-down',
    maxWidth: '75%',
    maxHeight: '75%',
  };

  const trackClick = (target?: string) => () => {
    track('click_button', {
      button: { name: 'partner_logo', linkURL: target ?? '' },
      pageSection: {
        sectionName: 'partner_logo_list',
      },
    });
  };

  const trackCtaClick = () => {
    track('click_button', {
      button: { name: 'promo_template_primary_cta', linkURL: cta?.target ?? '' },
      pageSection: {
        sectionName: 'partner_logo_list',
      },
    });
  };

  const imagePanel = () => (
    <ul css={styles.logoContainer}>
      {partnersCollection &&
        partnersCollection.map((partner) =>
          partner?.logo ? (
            <li key={partner.id} css={styles.logoItem}>
              {borders ? (
                <>
                  {partner.target ? (
                    <BasicCard
                      component={PreservedUtmLink}
                      href={partner.target}
                      openInNewTab={true}
                      onClick={trackClick(partner.target)}
                      css={[styles.logoWrapper]}
                    >
                      <Image {...partner.logo} style={imageStyle} />
                    </BasicCard>
                  ) : (
                    <div css={[styles.logoWrapper, styles.logoWrapperWithBorder]}>
                      <Image {...partner.logo} style={imageStyle} />
                    </div>
                  )}
                  <Typography2 variant="bodyPrimary" component="p" color="inherit">
                    <strong>{partner.name}</strong>
                  </Typography2>
                  <Typography2 variant="bodyPrimary" component="p" color="inherit">
                    {partner.description}
                  </Typography2>
                </>
              ) : (
                <div css={styles.logoWrapper}>
                  <Image {...partner.logo} style={imageStyle} />
                </div>
              )}
            </li>
          ) : null
        )}
    </ul>
  );

  const contentPanel = () => (
    <div>
      <Typography2 variant="titleSmall" component="h2" css={styles.tagline} color="inherit">
        {tagline}
      </Typography2>

      {description && (
        <Typography2 variant="bodyPrimary" component="p" css={styles.description} color="inherit">
          {description}
        </Typography2>
      )}

      {cta && (
        <div css={styles.ctaSection}>
          <PreservedUtmButton
            variant="ghost"
            component="a"
            href={cta?.target}
            icon={<ArrowNextIcon />}
            onClick={trackCtaClick}
            trackingName="logo_bar_logo"
          >
            {cta?.label}
          </PreservedUtmButton>
        </div>
      )}
    </div>
  );

  return (
    <ModuleLayout
      assembly={{ ...props, layout: MODULE_LAYOUTS.textTopImageBottom }}
      renderContent={contentPanel}
      renderImage={imagePanel}
      alignItems="center"
    />
  );
}

export default PartnerListModule;
